import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import { PieChart } from '@mui/x-charts';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { HEALTH_STATUS, RobotHealthTable } from 'models/fleet-management/FleetWidget';
import { FleetDataMissions, FleetDataRobots } from 'models/fleet-management/FleetDataSet';
import CustomTable from 'components/misc/CustomTable';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { FleetDashboardFilters } from 'models/fleet-management/FleetDataFilter';
import DashboardSection from '../DashboardSection';
import ReliabilitySection from './ReliabilitySection';
import WeeklyOutputChart from './WeeklyOutputChart';
import FleetSummary from '../FleetSummary';
import {
  BLUE_RAMP,
  FLEET_SECTION_GAP,
  HEALTH_STATUS_ORDER,
  HEALTH_TONES,
  StatusTone,
  topNWithOther,
} from 'utils/fleet-dashboard-theme';
import { rswVersionGroup } from 'utils/rsw-version';
import useShowDealerColumn from 'hooks/useShowDealerColumn';
import openRobotPage from 'utils/open-robot-page';

// const SERIAL_NUMBER_THRESHOLD = 20200929650; // below this = G1, equal or above this = G2+
const CHART_HEIGHT = 300;

/** The table prints the raw status, so anything unrecognised or missing falls back to inactive. */
const healthTone = (value: unknown): StatusTone => {
  const status = HEALTH_STATUS_ORDER.find((candidate) => candidate === value);
  return HEALTH_TONES[status ?? HEALTH_STATUS.GREY];
};

const FleetOverviewWidgets: React.FC<{
  filters: FleetDashboardFilters;
  robotsData: FleetDataRobots[];
  missionsData: FleetDataMissions[];
}> = ({ filters, robotsData, missionsData }) => {
  const { t } = useTranslation();

  const [healthFilter, setHealthFilter] = useState<HEALTH_STATUS>(HEALTH_STATUS.ALL);

  const softwareVersionData = useMemo(() => {
    const robots = robotsData ?? [];

    const counts = new Map<string, number>();

    for (const robot of robots) {
      const group =
        robot.software_version__rsw_software_version != null
          ? rswVersionGroup(robot.software_version__rsw_software_version)
          : t('unknown-value');
      counts.set(group, (counts.get(group) ?? 0) + 1);
    }

    const entries = Array.from(counts.entries()).map(([label, value]) => ({ label, value }));

    // One hue, stepped light to dark down the sorted list: the colours read as an order rather
    // than as five unrelated categories, and no slice needs a hue of its own.
    return topNWithOther(entries, 4, t('fleet-management-label-other')).map((entry, index) => ({
      id: entry.label,
      label: entry.label,
      value: entry.value,
      color: BLUE_RAMP[index % BLUE_RAMP.length],
    }));
  }, [robotsData, t]);

  // const getWarrantyYears = (robot) => {
  //     const robotGeneration = robot.hardware_version__robot_generation;
  //     let generation: number | null = null;

  //     if (robotGeneration !== null && robotGeneration !== undefined && robotGeneration !== "") {
  //         const parsedNumber = parseInt(robotGeneration, 10);
  //         if (!Number.isNaN(parsedNumber)) {
  //             generation = parsedNumber;
  //         }
  //     }

  //     if (generation === null) {
  //         const serial = parseInt(robot.serial_number, 10);
  //         if (!Number.isNaN(serial)) {
  //             generation = serial >= SERIAL_NUMBER_THRESHOLD ? 3 : 1;
  //         }
  //     }

  //     return generation === 1 ? 1 : 3;
  // }

  const robotTableRows: RobotHealthTable[] = useMemo(() => {
    const robots = robotsData ?? [];

    // const getWarrantyStatus = (robot) => {
    //     const deploymentStart = robot.deployment_start;
    //     if (!deploymentStart) return "-";

    //     const warrantyYears = getWarrantyYears(robot);

    //     const deploymentDate = new Date(deploymentStart);
    //     const expiry = new Date(deploymentDate.setFullYear(deploymentDate.getFullYear() + warrantyYears));

    //     const now = new Date();
    //     const daysRemaining = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    //     return {
    //         label: replaceArgsInMessage(t("fleet-management-label-warranty"), [daysRemaining]),
    //         expired: daysRemaining < 0,
    //     };
    // }

    return robots.map((r, index) => ({
      index: index + 1,
      id: r.id,
      name: r.name,
      serialNumber: r.serial_number,
      customer: r.customer ?? '-',
      dealer: r.dealer ?? '-',
      healthStatus: r.customer_journey_status ?? '-',
      generation: r.hardware_version__robot_generation ?? '-',
      // warranty: getWarrantyStatus(r),
      rswVersion: r.software_version__rsw_software_version ?? '-',
      lastMission: r.last_successful_mission_date
        ? r.last_successful_mission_date.slice(0, 10)
        : '-',
    }));
  }, [robotsData]);

  const filteredRobotTableRows = useMemo(() => {
    if (healthFilter === HEALTH_STATUS.ALL) return robotTableRows;
    return robotTableRows.filter((row) => row.healthStatus === healthFilter);
  }, [robotTableRows, healthFilter]);

  const dataLoaded = robotsData != null;
  const getStatus = dataLoaded ? HttpFetchStatus.Success : HttpFetchStatus.Loading;

  const showDealerColumn = useShowDealerColumn(filters);
  const allColumns: GridColDef[] = [
    { field: 'serialNumber', headerName: t('fleet-management-field-serial_number'), flex: 1 },
    {
      field: 'robot',
      headerName: t('fleet-management-field-robot'),
      flex: 1,
      valueGetter: (_, row) => {
        return `${row.name} - ${row.serialNumber}`;
      },
    },
    { field: 'customer', headerName: t('fleet-management-field-customer'), flex: 1 },
    { field: 'dealer', headerName: t('fleet-management-field-dealer'), flex: 1 },
    {
      field: 'healthStatus',
      headerName: t('fleet-management-field-robot-health_status'),
      flex: 0.7,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => {
        const tone = healthTone(params.value);

        // The status is what the table is scanned for, so the cell is filled with its tone
        // rather than outlined in it - the column reads as colour before it is read as words.
        return (
          <Chip
            label={params.value}
            size="small"
            sx={{
              fontWeight: 600,
              color: tone.ink,
              backgroundColor: tone.tint,
              border: `1px solid ${tone.mark}`,
            }}
          />
        );
      },
    },
    {
      field: 'generation',
      headerName: t('fleet-management-field-hardware_version__robot_generation'),
      flex: 0.7,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'rswVersion',
      headerName: t('fleet-management-field-software_version__rsw_software_version'),
      flex: 1,
    },
    {
      field: 'lastMission',
      headerName: t('fleet-management-field-last_successful_mission_date'),
      flex: 1.5,
    },
    {
      field: 'open',
      headerName: '',
      disableReorder: true,
      align: 'center',
      renderCell: (params) => (
        <Stack direction="row" width="100%" justifyContent="end" height="100%">
          <Button
            fullWidth
            onClick={(e) => {
              e.stopPropagation();
              openRobotPage(params.row.id);
            }}
          >
            {t('button-open')}
          </Button>
        </Stack>
      ),
    },
  ];
  const columns = showDealerColumn
    ? allColumns
    : allColumns.filter((column) => column.field !== 'dealer');

  const healthFilterChips = (
    <Stack direction="row" gap={0.75} flexWrap="wrap">
      {[
        { key: HEALTH_STATUS.ALL, label: t('fleet-management-filter-option-all') },
        { key: HEALTH_STATUS.GREEN, label: t('fleet-management-label-green') },
        { key: HEALTH_STATUS.ORANGE, label: t('fleet-management-label-orange') },
        { key: HEALTH_STATUS.RED, label: t('fleet-management-label-red') },
        { key: HEALTH_STATUS.GREY, label: t('fleet-management-label-grey') },
      ].map(({ key, label }) => {
        const isSelected = healthFilter === key;
        const tone = key === HEALTH_STATUS.ALL ? null : HEALTH_TONES[key];

        // Every chip wears its own status, selected or not; selection is the filled surface and
        // the heavier edge, so the filter row doubles as the legend for the column below it.
        return (
          <Chip
            key={key}
            size="small"
            label={label}
            onClick={() => setHealthFilter(key)}
            variant="outlined"
            icon={
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  ml: 1,
                  borderRadius: '50%',
                  backgroundColor: tone ? tone.mark : 'primary.main',
                  flexShrink: 0,
                }}
              />
            }
            sx={{
              cursor: 'pointer',
              fontWeight: isSelected ? 700 : 500,
              color: tone ? tone.ink : 'primary.main',
              borderColor: tone ? tone.mark : 'primary.main',
              borderWidth: isSelected ? 2 : 1,
              backgroundColor: isSelected ? (tone ? tone.tint : 'action.selected') : 'transparent',
            }}
          />
        );
      })}
    </Stack>
  );

  return (
    <Stack gap={FLEET_SECTION_GAP} width="100%">
      <FleetSummary filters={filters} robotsData={robotsData} />

      <ReliabilitySection filters={filters} />

      <Stack direction={{ xs: 'column', lg: 'row' }} gap={FLEET_SECTION_GAP} alignItems="stretch">
        <DashboardSection
          title={t('fleet-management-title-weekly-output')}
          caption={t('fleet-management-subtitle-weekly-output')}
          sx={{ flex: '1 1 0', minWidth: 0 }}
        >
          <WeeklyOutputChart missionsData={missionsData} />
        </DashboardSection>

        <DashboardSection
          title={t('fleet-management-subtitle-software-version')}
          sx={{ flex: '0 1 440px', minWidth: 340 }}
        >
          <PieChart
            series={[
              {
                data: softwareVersionData,
                innerRadius: 64,
                paddingAngle: 1,
                cornerRadius: 2,
                valueFormatter: (item) => `${item.value}`,
              },
            ]}
            height={CHART_HEIGHT}
            margin={{ left: 8, right: 132, top: 8, bottom: 8 }}
            slotProps={{
              legend: {
                direction: 'column',
                position: { vertical: 'middle', horizontal: 'right' },
                itemMarkWidth: 10,
                itemMarkHeight: 10,
                labelStyle: { fontSize: 12 },
              },
            }}
          />
        </DashboardSection>
      </Stack>

      <DashboardSection
        title={t('fleet-management-title-robots-health-status')}
        caption={t('fleet-management-subtitle-installed-robots')}
        actions={healthFilterChips}
      >
        <CustomTable
          getStatus={getStatus}
          dataLoaded={dataLoaded}
          failedToLoadMessage={t('fleet-management-error-loading-robots')}
          emptyDataMessage={t('fleet-management-no-robots')}
          data={filteredRobotTableRows}
          columns={columns}
          autoHeight
        />
      </DashboardSection>
    </Stack>
  );
};

export default React.memo(FleetOverviewWidgets);
